import React from 'react';
import { colors, fonts } from './tokens';

interface DimensionCardProps {
    label: string;
    subtitle?: string;
    percent: number;
    color: string;
    weeklyPts?: number;
    onClick?: () => void;
    /** No completed pass for this direction — show it as available, not as 0%. */
    locked?: boolean;
    /** Caption under the ring in the locked state, e.g. "не пройдено". */
    lockedLabel?: string;
}

const DimensionCard: React.FC<DimensionCardProps> = ({
    label,
    subtitle,
    percent,
    color,
    weeklyPts,
    onClick,
    locked = false,
    lockedLabel = 'Not taken',
}) => {
    const size = 180;
    const stroke = 14;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    // A locked ring is empty: there is no score to draw, only an invitation.
    const offset = locked ? c : c - (percent / 100) * c;
    const ringColor = locked ? '#3a3733' : color;

    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                background: 'transparent',
                borderRadius: 18,
                padding: '28px 12px 24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 14,
                cursor: onClick ? 'pointer' : 'default',
                position: 'relative',
                minWidth: 0,
                border: 'none',
                color: 'inherit',
                fontFamily: 'inherit',
                width: '100%',
            }}
        >
            <div style={{ position: 'relative', width: size, height: size }}>
                <div
                    style={{
                        position: 'absolute',
                        inset: stroke + 2,
                        borderRadius: '50%',
                        background: locked
                            ? 'none'
                            : `radial-gradient(circle at 50% 50%, ${color}3d 0%, ${color}1a 38%, transparent 72%)`,
                        pointerEvents: 'none',
                    }}
                />
                <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', position: 'relative' }}>
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={r}
                        stroke="#1a1816"
                        strokeWidth={stroke}
                        fill="none"
                        strokeDasharray={locked ? '3 7' : undefined}
                    />
                    {!locked && (
                        <circle
                            cx={size / 2}
                            cy={size / 2}
                            r={r}
                            stroke={ringColor}
                            strokeWidth={stroke - 6}
                            fill="none"
                            strokeLinecap="round"
                            strokeDasharray={c}
                            strokeDashoffset={offset}
                            style={{ filter: `drop-shadow(0 0 4px ${color}aa)` }}
                        />
                    )}
                </svg>
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'grid',
                        placeItems: 'center',
                    }}
                >
                    {locked ? (
                        <div
                            style={{
                                fontSize: 11,
                                fontFamily: fonts.mono,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                color: colors.muted,
                                textAlign: 'center',
                                maxWidth: 110,
                                lineHeight: 1.5,
                            }}
                        >
                            {lockedLabel}
                        </div>
                    ) : (
                        <div
                            style={{
                                fontSize: 34,
                                fontWeight: 600,
                                color: colors.textPrimary,
                                letterSpacing: '-0.04em',
                                lineHeight: 1,
                            }}
                        >
                            {percent}
                            <span
                                style={{
                                    fontSize: 16,
                                    color: colors.muted,
                                    fontWeight: 400,
                                }}
                            >
                                %
                            </span>
                        </div>
                    )}
                </div>
                {weeklyPts != null && weeklyPts > 0 && (
                    <div
                        style={{
                            position: 'absolute',
                            top: -4,
                            right: -10,
                            background: colors.pillBg,
                            border: `1px solid ${color}55`,
                            color,
                            fontSize: 11,
                            fontFamily: fonts.mono,
                            letterSpacing: '-0.01em',
                            padding: '5px 10px',
                            borderRadius: 999,
                            fontWeight: 500,
                        }}
                    >
                        +{weeklyPts} pts
                    </div>
                )}
            </div>
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div
                    style={{
                        fontSize: 16,
                        fontWeight: 500,
                        color: locked ? colors.muted : colors.textPrimary,
                        letterSpacing: '-0.01em',
                    }}
                >
                    {label}
                </div>
                {subtitle && (
                    <div
                        style={{
                            fontSize: 12,
                            color: colors.muted,
                            lineHeight: 1.4,
                            maxWidth: 200,
                            margin: '0 auto',
                        }}
                    >
                        {subtitle}
                    </div>
                )}
            </div>
        </button>
    );
};

export default DimensionCard;
